import { useState } from 'react';
import {
  Button, Chip, Stack, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Alert, Snackbar, IconButton, Tooltip, LinearProgress, Box, Divider,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import { EpcProjectSelect } from '../../components/EpcProjectSelect';
import { Add, Delete, Engineering } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { epcWorkOrderApi, type EpcWorkOrder, type VendorInfo, type WoLineInput } from '../../api/epcWorkOrder.api';
import { vendorsApi } from '../../api/vendors.api';
import { techniciansApi } from '../../api/technicians.api';
import { clientsApi } from '../../api/clients.api';
import { projectsApi } from '../../api/projects.api';
import { sitesApi } from '../../api/sites.api';
import { FileUploadField } from '../../components/FileUploadField';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

type WoLine = WoLineInput;
const emptyWoLine = (): WoLine => ({ description: '', hsnCode: '', rate: 0, quantity: 1, taxPercent: 0 });

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;
const statusColor = (s: string): 'default' | 'info' | 'success' | 'error' | 'warning' =>
  s === 'completed' ? 'success' : s === 'issued' ? 'info' : s === 'in_progress' ? 'warning' : s === 'cancelled' ? 'error' : 'default';

export function EpcWorkOrdersPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [projectId, setProjectId] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const pid = projectId ? Number(projectId) : undefined;
  const { data, isLoading } = useQuery({ queryKey: ['epc-work-orders', page, limit, pid], queryFn: () => epcWorkOrderApi.list({ page, limit, projectId: pid }) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['epc-work-orders'] });

  const columns: Column<EpcWorkOrder>[] = [
    { key: 'workOrderCode', label: 'Work Order', render: (r) => r.workOrderCode },
    { key: 'title', label: 'Title', render: (r) => <span>{r.title}{r.milestoneName ? <><br /><small style={{ color: '#888' }}>{r.milestoneName}</small></> : null}</span> },
    { key: 'assignee', label: 'Assigned to', render: (r) => [r.vendorName, r.technicianName].filter(Boolean).join(' · ') || '—' },
    { key: 'orderValue', label: 'Value', align: 'right', render: (r) => inr(r.orderValue) },
    { key: 'progressPct', label: 'Progress', render: (r) => <Box sx={{ minWidth: 90 }}><LinearProgress variant="determinate" value={r.progressPct} /><small>{r.progressPct}%</small></Box> },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status.replace('_', ' ')} color={statusColor(r.status)} /> },
  ];

  return (
    <>
      <PageHeader title="EPC Work Orders" subtitle="Assign a vendor to a site's SOW scope with priced BOQ line items (unit / rate / HSN + tax), then track execution."
        action={hasPermission('epc_work_order.create') && (
          <Button startIcon={<Add />} variant="contained" onClick={() => { setError(null); setCreateOpen(true); }}>New Work Order</Button>
        )} />

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <EpcProjectSelect value={projectId} onChange={(v) => { setProjectId(v); setPage(1); }} helperText={pid ? undefined : 'Select a project'} />
      </Stack>

      <DataTable columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id}
        actions={(r) => <Tooltip title="Open"><IconButton size="small" onClick={() => setDetailId(r.id)}><Engineering fontSize="small" /></IconButton></Tooltip>} />

      {createOpen && <CreateDialog onClose={() => setCreateOpen(false)}
        onDone={(id) => { setCreateOpen(false); invalidate(); setDetailId(id); }} onError={setError} error={error} />}
      {detailId && <DetailDialog id={detailId} onClose={() => setDetailId(null)} onChanged={invalidate} onToast={setToast} />}

      <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)}>
        <Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}

function CreateDialog({ onClose, onDone, onError, error }: {
  onClose: () => void; onDone: (id: number) => void; onError: (e: string) => void; error: string | null;
}) {
  const [clientId, setClientId] = useState<number | ''>('');
  const [projectId, setProjectId] = useState<number | ''>('');
  const [siteId, setSiteId] = useState<number | ''>('');
  const [vendorId, setVendorId] = useState('');
  const [technicianId, setTechnicianId] = useState('');
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [quotationUrl, setQuotationUrl] = useState('');
  const [lines, setLines] = useState<WoLine[]>([emptyWoLine()]);

  const clients = useQuery({ queryKey: ['clients', 'active'], queryFn: () => clientsApi.list({ limit: 500, status: 'active' }) });
  const projects = useQuery({ queryKey: ['projects', 'all'], queryFn: () => projectsApi.list({ limit: 1000 }) });
  const sites = useQuery({ queryKey: ['sites', 'all'], queryFn: () => sitesApi.list({ limit: 1000 }) });
  const { data: vendors } = useQuery({ queryKey: ['vendors', 'active'], queryFn: () => vendorsApi.list({ limit: 500, status: 'active' }) });
  const { data: technicians } = useQuery({ queryKey: ['technicians', 'active'], queryFn: () => techniciansApi.list({ limit: 500, status: 'active' }) });
  const { data: vinfo } = useQuery<VendorInfo | null>({ queryKey: ['wo-vendor-info', vendorId], queryFn: () => epcWorkOrderApi.vendorInfo(Number(vendorId)), enabled: Boolean(vendorId) });

  const clientProjects = (projects.data?.data ?? []).filter((p) => p.clientId === clientId);
  const projectSites = (sites.data?.data ?? []).filter((s) => s.projectId === projectId);
  const setLine = (i: number, k: keyof WoLine, v: unknown) => setLines((arr) => arr.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  const mut = useMutation({
    mutationFn: () => epcWorkOrderApi.create({
      projectId: Number(projectId), clientId: clientId ? Number(clientId) : undefined, siteId: siteId ? Number(siteId) : undefined,
      vendorId: vendorId ? Number(vendorId) : undefined, technicianId: technicianId ? Number(technicianId) : undefined,
      title, dueDate: dueDate || undefined, quotationUrl: quotationUrl || undefined,
      items: lines.filter((l) => l.description.trim()).map((l) => ({
        description: l.description, hsnCode: l.hsnCode ?? null, quantity: 1, rate: Number(l.rate) || 0, taxPercent: l.taxPercent != null ? Number(l.taxPercent) : 0,
      })),
    }),
    onSuccess: (wo) => onDone(wo.id),
    onError: (e) => onError(extractApiError(e)),
  });

  const subtotal = lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.rate) || 0), 0);
  const tax = lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.rate) || 0) * (Number(l.taxPercent) || 0) / 100, 0);
  const total = subtotal + tax;
  const canSave = Boolean(projectId) && Boolean(title.trim()) && (Boolean(vendorId) || Boolean(technicianId));

  return (
    <Dialog open fullWidth maxWidth="lg" onClose={onClose}>
      <DialogTitle>New Work Order</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Client → Project → Site</Typography></Divider>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField select size="small" label="Client *" value={clientId} sx={{ minWidth: 220 }}
              onChange={(e) => { setClientId(e.target.value ? Number(e.target.value) : ''); setProjectId(''); setSiteId(''); setLines([]); }}>
              {(clients.data?.data ?? []).map((c) => <MenuItem key={c.id} value={c.id}>{c.clientName}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Project *" value={projectId} disabled={!clientId} sx={{ minWidth: 240 }}
              onChange={(e) => { setProjectId(e.target.value ? Number(e.target.value) : ''); setSiteId(''); }}>
              {clientProjects.map((p) => <MenuItem key={p.id} value={p.id}>{p.projectCode} — {p.projectName}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Site" value={siteId} disabled={!projectId} sx={{ minWidth: 220 }}
              onChange={(e) => setSiteId(e.target.value ? Number(e.target.value) : '')}>
              {projectSites.map((s) => <MenuItem key={s.id} value={s.id}>{s.siteName} ({s.siteCode})</MenuItem>)}
            </TextField>
          </Stack>

          <TextField label="Title *" size="small" value={title} onChange={(e) => setTitle(e.target.value)} sx={{ maxWidth: 500 }} />

          <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Vendor / Technician</Typography></Divider>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="flex-start">
            <TextField select size="small" label="Vendor / Subcontractor" value={vendorId} onChange={(e) => setVendorId(e.target.value)} sx={{ minWidth: 240 }}>
              <MenuItem value="">— none —</MenuItem>
              {(vendors?.data ?? []).map((v) => <MenuItem key={v.id} value={v.id}>{v.vendorName}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Technician" value={technicianId} onChange={(e) => setTechnicianId(e.target.value)} sx={{ minWidth: 200 }}>
              <MenuItem value="">— none —</MenuItem>
              {(technicians?.data ?? []).map((t) => <MenuItem key={t.id} value={t.id}>{t.technicianName}</MenuItem>)}
            </TextField>
            <TextField label="Due date" type="date" size="small" InputLabelProps={{ shrink: true }} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </Stack>
          {vinfo && (
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1.5, p: 1.5 }}>
              <Typography variant="body2"><strong>{vinfo.vendorName}</strong>{vinfo.serviceCategory ? ` · ${vinfo.serviceCategory}` : ''}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {[vinfo.gstNumber && `GST: ${vinfo.gstNumber}`, vinfo.panNumber && `PAN: ${vinfo.panNumber}`, vinfo.mobile, vinfo.email].filter(Boolean).join(' · ') || '—'}
              </Typography>
              {vinfo.address && <Typography variant="caption" color="text.secondary">{vinfo.address}</Typography>}
            </Box>
          )}

          <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Scope of Work</Typography></Divider>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Scope of Work *</TableCell><TableCell>HSN Code</TableCell>
              <TableCell align="right">Rate</TableCell><TableCell align="right">GST%</TableCell><TableCell align="right">Amount</TableCell><TableCell />
            </TableRow></TableHead>
            <TableBody>
              {lines.map((l, i) => (
                <TableRow key={i}>
                  <TableCell><TextField size="small" variant="standard" fullWidth value={l.description} onChange={(e) => setLine(i, 'description', e.target.value)} /></TableCell>
                  <TableCell><TextField size="small" variant="standard" value={l.hsnCode ?? ''} onChange={(e) => setLine(i, 'hsnCode', e.target.value)} sx={{ width: 100 }} /></TableCell>
                  <TableCell align="right"><TextField size="small" variant="standard" type="number" value={l.rate} onChange={(e) => setLine(i, 'rate', Number(e.target.value))} sx={{ width: 100 }} /></TableCell>
                  <TableCell align="right"><TextField size="small" variant="standard" type="number" value={l.taxPercent ?? 0} onChange={(e) => setLine(i, 'taxPercent', Number(e.target.value))} sx={{ width: 64 }} /></TableCell>
                  <TableCell align="right">{inr((Number(l.rate) || 0) * (1 + (Number(l.taxPercent) || 0) / 100))}</TableCell>
                  <TableCell><IconButton size="small" color="error" onClick={() => setLines((arr) => arr.filter((_, j) => j !== i))} disabled={lines.length === 1}><Delete fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box><Button size="small" startIcon={<Add />} onClick={() => setLines((arr) => [...arr, emptyWoLine()])}>Add line</Button></Box>

          <FileUploadField label="Quotation (optional)" accept=".pdf,image/*,.xls,.xlsx" value={quotationUrl} onChange={(url) => setQuotationUrl(url ?? '')} />

          <Stack alignItems="flex-end" spacing={0.25}>
            <Typography variant="body2" color="text.secondary">Sub-total: {inr(subtotal)}</Typography>
            <Typography variant="body2" color="text.secondary">GST: {inr(tax)}</Typography>
            <Typography variant="subtitle1">Work Order Value: {inr(total)}</Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">Assign at least a vendor or a technician.</Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSave || mut.isPending} onClick={() => mut.mutate()}>Create Work Order</Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailDialog({ id, onClose, onChanged, onToast }: { id: number; onClose: () => void; onChanged: () => void; onToast: (m: string) => void }) {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [err, setErr] = useState<string | null>(null);
  const { data: wo } = useQuery({ queryKey: ['epc-work-order', id], queryFn: () => epcWorkOrderApi.get(id) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['epc-work-order', id] }); onChanged(); };
  const act = (fn: () => Promise<unknown>, msg: string) => fn().then(() => { refresh(); onToast(msg); }).catch((e) => setErr(extractApiError(e)));

  if (!wo) return null;
  const canProgress = (wo.status === 'issued' || wo.status === 'in_progress') && hasPermission('epc_work_order.update');

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{wo.workOrderCode} <Chip size="small" sx={{ ml: 1 }} label={wo.status.replace('_', ' ')} color={statusColor(wo.status)} />
        <Typography variant="body2" color="text.secondary">{wo.title}{wo.milestoneName ? ` · ${wo.milestoneName}` : ''}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}
        <Stack spacing={1}>
          <Typography variant="body2"><b>Assigned to:</b> {[wo.vendorName, wo.technicianName].filter(Boolean).join(' · ') || '—'}</Typography>
          {(wo.items?.length ?? 0) > 0 && (
            <Table size="small" sx={{ my: 1 }}>
              <TableHead><TableRow><TableCell>Description</TableCell><TableCell>Unit</TableCell><TableCell>HSN</TableCell><TableCell align="right">Qty</TableCell><TableCell align="right">Rate</TableCell><TableCell align="right">GST%</TableCell><TableCell align="right">Amount</TableCell></TableRow></TableHead>
              <TableBody>
                {(wo.items ?? []).map((it) => (
                  <TableRow key={it.id}>
                    <TableCell>{it.description}</TableCell><TableCell>{it.unit ?? '—'}</TableCell><TableCell>{it.hsnCode ?? '—'}</TableCell>
                    <TableCell align="right">{it.quantity}</TableCell><TableCell align="right">{inr(it.rate)}</TableCell><TableCell align="right">{it.taxPercent}%</TableCell>
                    <TableCell align="right">{inr(it.amount + it.taxAmount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Typography variant="body2"><b>Order value:</b> {inr(wo.orderValue)}{wo.taxTotal ? ` (incl. GST ${inr(wo.taxTotal)})` : ''}</Typography>
          {wo.quotationUrl && <Typography variant="body2"><b>Quotation:</b> <a href={wo.quotationUrl} target="_blank" rel="noreferrer">View</a></Typography>}
          {wo.scope && <Typography variant="body2"><b>Scope:</b> {wo.scope}</Typography>}
          {wo.dueDate && <Typography variant="body2"><b>Due:</b> {new Date(wo.dueDate).toLocaleDateString('en-IN')}</Typography>}
          {wo.completedDate && <Typography variant="body2"><b>Completed:</b> {new Date(wo.completedDate).toLocaleDateString('en-IN')}</Typography>}
          <Box sx={{ mt: 1 }}><Typography variant="caption">Progress: {wo.progressPct}%</Typography><LinearProgress variant="determinate" value={wo.progressPct} /></Box>
          {canProgress && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              {[25, 50, 75, 100].map((p) => (
                <Button key={p} size="small" variant="outlined" onClick={() => act(() => epcWorkOrderApi.setProgress(id, { progressPct: p }), `Progress set to ${p}%`)}>{p}%</Button>
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {wo.status === 'draft' && hasPermission('epc_work_order.delete') && <Button color="error" onClick={() => act(() => epcWorkOrderApi.remove(id).then(() => onClose()), 'Work order deleted')}>Delete</Button>}
        {wo.status === 'draft' && hasPermission('epc_work_order.issue') && <Button variant="contained" onClick={() => act(() => epcWorkOrderApi.issue(id), 'Work order issued')}>Issue</Button>}
        {(wo.status === 'issued' || wo.status === 'in_progress') && hasPermission('epc_work_order.update') && (
          <Button color="error" onClick={() => { const r = window.prompt('Cancel reason (optional)?') ?? undefined; act(() => epcWorkOrderApi.cancel(id, r), 'Work order cancelled'); }}>Cancel WO</Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
